"use client";

import { Card, CardContent } from "@/components/shared/ui/card";
import { SUPPLIER_FILTER_ID } from "@/lib/shop/shop-constants";
import { ShopMenuPromotion } from "@/lib/shop/shop-types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";

type MenuPromotionCarouselProps = {
    menuPromotions: ShopMenuPromotion[];
    showTitle?: boolean;
};

export default function MenuPromotionCarousel({
    menuPromotions,
    showTitle = true
}: MenuPromotionCarouselProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [isTextTruncated, setIsTextTruncated] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isHovered) return;

        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % menuPromotions.length);
        }, 15000);
        return () => clearInterval(timer);
    }, [menuPromotions.length, isHovered]);

    useEffect(() => {
        if (textRef.current) {
            const element = textRef.current;
            setIsTextTruncated(element.scrollHeight > element.clientHeight);
        }
    }, [currentIndex]);

    if (menuPromotions.length === 0) {
        return null;
    }

    const navigate = (direction: "prev" | "next") => {
        if (direction === "prev") {
            setCurrentIndex((prev) => (prev - 1 + menuPromotions.length) % menuPromotions.length);
        } else {
            setCurrentIndex((prev) => (prev + 1) % menuPromotions.length);
        }
    };

    const currentPromotion = menuPromotions[currentIndex];

    if (!currentPromotion) {
        return null;
    }

    const handleClick = (e: React.MouseEvent, direction: "prev" | "next") => {
        e.preventDefault();
        e.stopPropagation();
        navigate(direction);
    };

    const handleTextHover = () => {
        if (isTextTruncated) {
            setShowTooltip(true);
        }
    };

    const handleTextLeave = () => {
        setShowTooltip(false);
    };

    const cardContent = (
        <Card className="h-32 rounded-lg bg-white transition-colors">
            <CardContent className={`h-full p-6 ${showTitle ? "cursor-pointer" : ""}`}>
                <div className="flex h-full items-center justify-between">
                    {menuPromotions.length > 1 ? (
                        <button
                            onClick={(e) => handleClick(e, "prev")}
                            className="rounded-full p-2 transition-colors hover:bg-gray-100"
                            aria-label="Previous promotion"
                        >
                            <ChevronLeft className="h-6 w-6" />
                        </button>
                    ) : null}

                    <div className="mx-2 flex h-full w-full items-center space-x-6 overflow-hidden">
                        <div className="relative overflow-hidden">
                            {menuPromotions.map((promotion, idx) => (
                                <div
                                    key={promotion.supplierID}
                                    className={`absolute left-0 top-0 flex items-center transition-all duration-500 ease-in-out ${
                                        idx === currentIndex
                                            ? "translate-x-0 opacity-100"
                                            : idx < currentIndex
                                              ? "-translate-x-full opacity-0"
                                              : "translate-x-full opacity-0"
                                    }`}
                                    style={{
                                        width: "100%",
                                        minHeight: "80px"
                                    }}
                                >
                                    <div className="flex w-full items-center space-x-6">
                                        {promotion.supplierLogoURL !== undefined && showTitle ? (
                                            <div className="flex-shrink-0">
                                                <Image
                                                    src={promotion.supplierLogoURL}
                                                    alt={promotion.supplierName}
                                                    width={80}
                                                    height={80}
                                                    className="object-contain"
                                                />
                                            </div>
                                        ) : showTitle ? (
                                            <h3 className="flex-shrink-0 text-xl font-bold text-gray-900">
                                                {promotion.supplierName}
                                            </h3>
                                        ) : null}

                                        <div className="relative min-w-0 flex-1">
                                            <p
                                                ref={idx === currentIndex ? textRef : null}
                                                className="line-clamp-3 text-lg font-medium text-gray-600"
                                            >
                                                {promotion.description}
                                            </p>
                                            {isTextTruncated && idx === currentIndex && (
                                                <span
                                                    className="absolute bottom-0 right-0 bg-white pl-1 text-lg font-medium text-blue-600"
                                                    onMouseEnter={handleTextHover}
                                                    onMouseLeave={handleTextLeave}
                                                >
                                                    ...more
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div
                                className="invisible flex w-full items-center space-x-6"
                                style={{ minHeight: "80px" }}
                            >
                                {currentPromotion.supplierLogoURL !== undefined && showTitle ? (
                                    <div className="flex-shrink-0">
                                        <div style={{ width: 80, height: 80 }} />
                                    </div>
                                ) : showTitle ? (
                                    <h3 className="flex-shrink-0 text-xl font-bold text-gray-900">
                                        {currentPromotion.supplierName}
                                    </h3>
                                ) : null}
                                <div className="min-w-0 flex-1">
                                    <p className="line-clamp-3 text-lg font-medium text-gray-600">
                                        {currentPromotion.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {menuPromotions.length > 1 ? (
                        <button
                            onClick={(e) => handleClick(e, "next")}
                            className="rounded-full p-2 transition-colors hover:bg-gray-100"
                            aria-label="Next promotion"
                        >
                            <ChevronRight className="h-6 w-6" />
                        </button>
                    ) : null}
                </div>
            </CardContent>
        </Card>
    );

    return (
        <div
            ref={containerRef}
            className="relative mx-auto w-full max-w-4xl items-center"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {showTitle ? (
                <Link
                    href={`/${currentPromotion.state}/shop?${SUPPLIER_FILTER_ID}=${currentPromotion.supplierID}`}
                    className="block"
                >
                    {cardContent}
                </Link>
            ) : (
                cardContent
            )}

            {showTooltip && isTextTruncated && (
                <div
                    className="absolute left-0 top-0 z-50 w-full rounded-lg border border-gray-200 bg-white p-4 text-lg font-medium text-gray-600 shadow-xl"
                    onMouseEnter={handleTextHover}
                    onMouseLeave={handleTextLeave}
                    style={{ minHeight: "128px" }}
                >
                    <div className="flex items-center space-x-6">
                        {currentPromotion.supplierLogoURL !== undefined && showTitle ? (
                            <div className="flex-shrink-0">
                                <Image
                                    src={currentPromotion.supplierLogoURL}
                                    alt={currentPromotion.supplierName}
                                    width={80}
                                    height={80}
                                    className="object-contain"
                                />
                            </div>
                        ) : showTitle ? (
                            <h3 className="flex-shrink-0 text-xl font-bold text-gray-900">
                                {currentPromotion.supplierName}
                            </h3>
                        ) : null}
                        <div className="min-w-0 flex-1">
                            <p className="text-lg font-medium text-gray-600">
                                {currentPromotion.description}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {menuPromotions.length > 1 ? (
                <div className="mt-4 flex justify-center gap-2">
                    {menuPromotions.map((_, idx) => (
                        <button
                            key={idx}
                            className={`h-2 w-2 rounded-full transition-colors duration-300 ${
                                idx === currentIndex ? "bg-blue-600" : "bg-gray-300"
                            }`}
                            onClick={(e) => {
                                e.preventDefault();
                                setCurrentIndex(idx);
                            }}
                            aria-label={`Go to promotion ${idx + 1}`}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
